import { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getAnalyticsCenterData } from "../../../api/analyticsCenterService";

export default function AnalyticsCenterPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);

            try {
                const result = await getAnalyticsCenterData();
                setData(result);
            } catch (err) {
                console.error("Analytics Center load failed", err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    if (loading) {
        return <div style={{ padding: 20 }}>Loading Analytics Center...</div>;
    }

    if (!data) {
        return <div style={{ padding: 20 }}>Analytics data not available.</div>;
    }

    return (
        <div style={{ padding: 20 }}>
            <h2>Analytics Center</h2>

            <p style={{ color: "#666" }}>
                Unified business intelligence dashboard for logistics, blockchain, AI, IoT and trust analytics.
            </p>

            <div style={gridStyle}>
                <Kpi title="Revenue" value={`�${data.revenue}`} color="#EF6C00" />
                <Kpi title="Orders" value={data.orders} color="#43A047" />
                <Kpi title="Shipments" value={data.shipments} color="#1976D2" />
                <Kpi title="IoT Online" value={`${data.onlineDevices}/${data.iotDevices}`} color="#7B1FA2" />
                <Kpi title="Blockchain Events" value={data.blockchainEvents} color="#546E7A" />
                <Kpi title="Users" value={data.users} color="#3949AB" />
                <Kpi title="Average Trust" value={`${data.averageTrustScore}%`} color="#2E7D32" />
                <Kpi title="AI Risk" value={`${data.aiRisk}%`} color="#C62828" />
            </div>

            <div style={chartsGridStyle}>
                <ChartBox title="Revenue Trend">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.revenueTrend}>
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="value" />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartBox>

                <ChartBox title="Orders Trend">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.ordersTrend}>
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartBox>

                <ChartBox title="Shipment Status">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data.shipmentStatus}
                                dataKey="value"
                                nameKey="name"
                                outerRadius={90}
                                label
                            >
                                <Cell />
                                <Cell />
                                <Cell />
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartBox>

                <ChartBox title="Trust Score Distribution">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.trustScoreData}>
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartBox>
            </div>
        </div>
    );
}

function Kpi({ title, value, color }) {
    return (
        <div style={{ ...kpiStyle, borderTop: `6px solid ${color}` }}>
            <div style={{ color: "#666", fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 32, fontWeight: "bold", marginTop: 10 }}>
                {value}
            </div>
        </div>
    );
}

function ChartBox({ title, children }) {
    return (
        <div style={chartBoxStyle}>
            <h3>{title}</h3>
            <div style={{ height: 280 }}>
                {children}
            </div>
        </div>
    );
}

const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 20,
};

const chartsGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 20,
    marginTop: 24,
};

const kpiStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const chartBoxStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};